import { i18n } from '../i18n.js';
/**
 * Scoped opponent-reconnect countdown.
 *
 * The server owns the grace deadline. This projection keeps one DOM node and
 * one timer, and accepts notices only for the opponent in the installed match.
 */

let ReconnectStatus = {};

ReconnectStatus = (function () {
    'use strict';

    const ELEMENT_ID = 'opponent-reconnect-status';
    let timer = null;
    let activeScope = null;
    let localDeadlineAt = null;
    let lastRenderedSeconds = null;

    function normalizeId(value) {
        if (typeof value !== 'string' || !value || value.length > 128) return null;
        return value === value.trim() ? value : null;
    }

    function matchingScope(payload, gameState) {
        const roomId = normalizeId(payload?.roomId);
        const matchId = normalizeId(payload?.matchId);
        const playerId = normalizeId(payload?.playerId);
        if (!roomId || !matchId || !playerId) return null;
        if (
            gameState?.roomId !== roomId
            || gameState?.matchId !== matchId
            || gameState?.opponent?.id !== playerId
        ) return null;
        return { roomId, matchId, playerId };
    }

    function removeTimer() {
        if (timer) clearInterval(timer);
        timer = null;
    }

    function clear() {
        removeTimer();
        activeScope = null;
        localDeadlineAt = null;
        lastRenderedSeconds = null;
        document.getElementById(ELEMENT_ID)?.remove();
    }

    function ensureElement() {
        let element = document.getElementById(ELEMENT_ID);
        if (element) return element;
        element = document.createElement('div');
        element.id = ELEMENT_ID;
        element.className = 'opponent-reconnect-status';
        element.setAttribute('role', 'status');
        element.setAttribute('aria-live', 'polite');
        element.setAttribute('aria-atomic', 'true');
        document.body.appendChild(element);
        return element;
    }

    function render() {
        const element = ensureElement();
        const seconds = Math.max(
            0,
            Math.ceil((Number(localDeadlineAt) - Date.now()) / 1000),
        );
        if (seconds === lastRenderedSeconds && element.textContent) return;
        lastRenderedSeconds = seconds;
        const translated = i18n?.t?.('game.opponentReconnecting', { seconds });
        element.textContent = translated && translated !== 'game.opponentReconnecting'
            ? translated
            : `Opponent reconnecting · ${seconds}s`;
        if (seconds === 0) removeTimer();
    }

    function show(payload, gameState) {
        const scope = matchingScope(payload, gameState);
        const graceExpiresAt = Number(payload?.graceExpiresAt);
        const serverNow = Number(payload?.serverNow);
        if (
            !scope
            || !Number.isFinite(graceExpiresAt)
            || !Number.isFinite(serverNow)
            || graceExpiresAt < serverNow
        ) return false;

        activeScope = scope;
        const now = Date.now();
        localDeadlineAt = now + (graceExpiresAt - serverNow);
        lastRenderedSeconds = null;
        removeTimer();
        render();
        if (localDeadlineAt > now) {
            timer = setInterval(render, 250);
        }
        return true;
    }

    function handle(payload, gameState) {
        const scope = matchingScope(payload, gameState);
        if (!scope) return false;
        if (payload?.status === 'disconnected') return show(payload, gameState);
        if (
            payload?.status === 'connected'
            && activeScope
            && activeScope.roomId === scope.roomId
            && activeScope.matchId === scope.matchId
            && activeScope.playerId === scope.playerId
        ) {
            clear();
            return true;
        }
        return false;
    }

    return { clear, handle, show };
})();

export { ReconnectStatus };
